import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import InputMask from 'react-input-mask';
import { createClient } from '@supabase/supabase-js';
import { customerSchema, type Customer } from '../../types/customer';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseKey);

interface CustomerFormProps {
  initialData?: Customer;
  onSubmit: (data: Customer) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const CustomerForm: React.FC<CustomerFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false
}) => {
  const [customerType, setCustomerType] = useState(initialData?.type || 'PF');

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm<Customer>({
    resolver: zodResolver(customerSchema),
    defaultValues: initialData || {
      type: 'PF',
      status: 'active'
    }
  });

  const fetchAddressByCep = async (cep: string) => {
    if (cep.replace(/\D/g, '').length !== 8) return;

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();

      if (!data.erro) {
        setValue('address_street', data.logradouro);
        setValue('address_neighborhood', data.bairro);
        setValue('address_city', data.localidade);
        setValue('address_state', data.uf);
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
    }
  };

  const onSubmitForm = async (data: Customer) => {
    await onSubmit({
      ...data,
      type: customerType,
      status: data.status || 'active'
    });

    // Supabase insert
    const { error } = await supabase.from('clientes').insert({
      nome: data.name,
      document: data.document,
      email: data.email,
      phone: data.phone,
      whatsapp: data.whatsapp,
      cep: data.address_zip,
      street: data.address_street,
      number: data.address_number,
      complement: data.address_complement,
      neighborhood: data.address_neighborhood,
      city: data.address_city,
      state: data.address_state,
      notes: data.notes,
      type: customerType,
      status: data.status || 'active',
      user_id: (await supabase.auth.getUser()).data.user?.id || null
    });

    if (error) {
      console.error('Erro ao salvar no Supabase:', error.message);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-6 bg-white p-6 rounded-lg shadow">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Cliente</label>
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                value="PF"
                checked={customerType === 'PF'}
                onChange={(e) => setCustomerType(e.target.value as 'PF' | 'PJ')}
                className="mr-2"
              />
              Pessoa Física
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="PJ"
                checked={customerType === 'PJ'}
                onChange={(e) => setCustomerType(e.target.value as 'PF' | 'PJ')}
                className="mr-2"
              />
              Pessoa Jurídica
            </label>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {customerType === 'PF' ? 'Nome Completo' : 'Razão Social'}
          </label>
          <input
            type="text"
            {...register('name')}
            className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.name ? 'border-red-300' : 'border-gray-300'}"
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {customerType === 'PF' ? 'CPF' : 'CNPJ'}
          </label>
          <InputMask
            mask={customerType === 'PF' ? '999.999.999-99' : '99.999.999/9999-99'}
            {...register('document')}
            className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.document ? 'border-red-300' : 'border-gray-300'}"
          />
          {errors.document && (
            <p className="mt-1 text-sm text-red-600">{errors.document.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input
            type="email"
            {...register('email')}
            className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.email ? 'border-red-300' : 'border-gray-300'}"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
          <InputMask
            mask="(99) 99999-9999"
            {...register('phone')}
            className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.phone ? 'border-red-300' : 'border-gray-300'}"
          />
          {errors.phone && (
            <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp</label>
          <InputMask
            mask="(99) 99999-9999"
            {...register('whatsapp')}
            className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.whatsapp ? 'border-red-300' : 'border-gray-300'}"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">CEP</label>
          <InputMask
            mask="99999-999"
            {...register('address_zip')}
            onBlur={(e) => fetchAddressByCep(e.target.value)}
            className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.address_zip ? 'border-red-300' : 'border-gray-300'}"
          />
          {errors.address_zip && (
            <p className="mt-1 text-sm text-red-600">{errors.address_zip.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Rua</label>
          <input
            type="text"
            {...register('address_street')}
            className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.address_street ? 'border-red-300' : 'border-gray-300'}"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Número</label>
          <input
            type="text"
            {...register('address_number')}
            className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.address_number ? 'border-red-300' : 'border-gray-300'}"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Complemento</label>
          <input
            type="text"
            {...register('address_complement')}
            className="w-full px-3 py-2 border rounded-md shadow-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Bairro</label>
          <input
            type="text"
            {...register('address_neighborhood')}
            className="w-full px-3 py-2 border rounded-md shadow-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Cidade</label>
          <input
            type="text"
            {...register('address_city')}
            className="w-full px-3 py-2 border rounded-md shadow-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
          <input
            type="text"
            {...register('address_state')}
            className="w-full px-3 py-2 border rounded-md shadow-sm"
          />
        </div>

        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
          <textarea
            {...register('notes')}
            rows={4}
            className="w-full px-3 py-2 border rounded-md shadow-sm"
          />
        </div>
      </div>

      <div className="flex justify-end space-x-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          disabled={isLoading}
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
        >
          {isLoading ? 'Salvando...' : 'Salvar'}
        </button>
      </div>
    </form>
  );
};

export default CustomerForm;
